// Typed mock data for EPIC-005/006/007 (Search / Preview / Apply Work
// Orders). Draft only — no API-backed source yet. Shapes mirror `work_order`
// (src/db/schema/handover/work-order.ts) plus the extra NetSuite-style
// fields the row detail drawer shows.

import type { EnumOption } from "@/lib/enums";
import { REFERENCE_ITEMS, REFERENCE_LOCATIONS } from "@/mocks/mass-update-reference";

export const WORK_ORDER_STATUSES = [
  "Not Started",
  "In Process",
  "Build Complete",
  "Closed",
] as const;
export type WorkOrderStatus = (typeof WORK_ORDER_STATUSES)[number];

export const WORK_ORDER_STATUS_OPTIONS: readonly EnumOption[] = WORK_ORDER_STATUSES.map(
  (status) => ({ value: status, label: status }),
);

export interface WorkOrder {
  workOrderId: string;
  internalId: string;
  workOrderNo: string;
  itemCode: string;
  itemDescription: string;
  locationCode: string;
  locationName: string;
  status: WorkOrderStatus;
  startDate: string; // ISO yyyy-mm-dd
  endDate: string; // ISO yyyy-mm-dd
  quantity: number;
  firmed: boolean;
  memo: string;
}

function workOrder(
  n: number,
  itemIndex: number,
  locationCode: string,
  status: WorkOrderStatus,
  quantity: number,
  startDate: string,
  endDate: string,
  firmed: boolean,
  memo: string,
): WorkOrder {
  const item = REFERENCE_ITEMS[itemIndex % REFERENCE_ITEMS.length] ?? {
    code: "UNKNOWN",
    description: "Unknown item",
  };
  const itemCode = item.code;
  const itemDescription = item.description;
  const location = REFERENCE_LOCATIONS.find((candidate) => candidate.code === locationCode) ?? {
    code: locationCode,
    name: locationCode,
  };
  return {
    workOrderId: `WO-L-${2000 + n}`,
    internalId: String(77000 + n),
    workOrderNo: `WO-${5200 + n}`,
    itemCode,
    itemDescription,
    locationCode: location.code,
    locationName: location.name,
    status,
    startDate,
    endDate,
    quantity,
    firmed,
    memo,
  };
}

// Fixed sample work orders — stands in for what a real search would pull
// from NetSuite. Same shape and values every load.
export const WORK_ORDERS: readonly WorkOrder[] = [
  workOrder(1, 0, "PROD-FL1", "In Process", 40, "2026-09-10", "2026-09-24", true, "Rush order"),
  workOrder(2, 1, "PROD-FL1", "Not Started", 25, "2026-09-20", "2026-10-02", false, ""),
  workOrder(3, 2, "PROD-FL2", "Build Complete", 12, "2026-08-28", "2026-09-15", true, ""),
  workOrder(4, 3, "PROD-FL1", "In Process", 60, "2026-09-12", "2026-09-29", true, "Second shift"),
  workOrder(5, 4, "PROD-FL2", "Not Started", 18, "2026-09-22", "2026-10-06", false, ""),
  workOrder(6, 5, "PROD-FL1", "Closed", 30, "2026-08-15", "2026-08-29", true, ""),
  workOrder(7, 0, "PROD-FL2", "In Process", 20, "2026-09-14", "2026-09-30", false, "Awaiting QC"),
  workOrder(
    8,
    1,
    "QC-HOLD",
    "Not Started",
    10,
    "2026-09-25",
    "2026-10-09",
    false,
    "On hold for spec review",
  ),
  workOrder(9, 2, "PROD-FL1", "Build Complete", 45, "2026-08-30", "2026-09-13", true, ""),
  workOrder(10, 3, "PROD-FL2", "In Process", 35, "2026-09-16", "2026-10-01", true, ""),
  workOrder(11, 4, "PROD-FL1", "Not Started", 22, "2026-09-27", "2026-10-11", false, ""),
  workOrder(12, 5, "MAIN-WH", "Closed", 15, "2026-08-10", "2026-08-24", true, "Rework batch"),
];

export interface WorkOrderSearchFilters {
  workOrderNo: string;
  itemCode: string;
  locationCode: string;
  status: string;
  startDateFrom: string;
  startDateTo: string;
  endDateFrom: string;
  endDateTo: string;
}

export const EMPTY_WORK_ORDER_SEARCH_FILTERS: WorkOrderSearchFilters = {
  workOrderNo: "",
  itemCode: "",
  locationCode: "",
  status: "",
  startDateFrom: "",
  startDateTo: "",
  endDateFrom: "",
  endDateTo: "",
};

export interface WorkOrderSetValues {
  status: string;
  locationCode: string;
  startDate: string;
  endDate: string;
  firmed: string; // "" | "true" | "false"
  memo: string;
}

export const EMPTY_WORK_ORDER_SET_VALUES: WorkOrderSetValues = {
  status: "",
  locationCode: "",
  startDate: "",
  endDate: "",
  firmed: "",
  memo: "",
};

export function matchesWorkOrderSearchFilters(
  candidate: WorkOrder,
  filters: WorkOrderSearchFilters,
): boolean {
  if (
    filters.workOrderNo &&
    !candidate.workOrderNo.toLowerCase().includes(filters.workOrderNo.toLowerCase())
  ) {
    return false;
  }
  if (filters.itemCode && candidate.itemCode !== filters.itemCode) return false;
  if (filters.locationCode && candidate.locationCode !== filters.locationCode) return false;
  if (filters.status && candidate.status !== filters.status) return false;
  if (filters.startDateFrom && candidate.startDate < filters.startDateFrom) return false;
  if (filters.startDateTo && candidate.startDate > filters.startDateTo) return false;
  if (filters.endDateFrom && candidate.endDate < filters.endDateFrom) return false;
  if (filters.endDateTo && candidate.endDate > filters.endDateTo) return false;
  return true;
}

export const searchWorkOrdersKeys = {
  all: ["mocks", "search-work-orders", "workOrders"] as const,
};

export function fetchWorkOrders(): Promise<WorkOrder[]> {
  return Promise.resolve([...WORK_ORDERS]);
}
